import React, { useState, useEffect, useRef } from 'react';
import { Mail, Send, Paperclip, X, CheckCircle, Clock, AlertCircle, Eye, MousePointer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { config } from '@/src/config';
import { useFormattedDate } from '../hooks/useUserTimezone';
import { authFetch } from '@/lib/authFetch';
import { inboxEmailAPI, type MailboxStatus } from '@/services/api/inboxEmail';

interface Email {
  id: number;
  message_id: string;
  from_email: string;
  to_email: string;
  cc_emails: string[];
  subject: string;
  body_html: string;
  body_text: string;
  direction: 'inbound' | 'outbound';
  status: 'queued' | 'sent' | 'delivered' | 'opened' | 'clicked' | 'bounced' | 'failed' | 'spam';
  has_attachments: boolean;
  attachments_count: number;
  attachments: Array<{filename: string; size: number; content_type: string; url: string}>;
  opened_at?: string;
  clicked_at?: string;
  created_at: string;
  sent_at?: string;
  delivered_at?: string;
}

interface EmailConversationViewProps {
  referralId: string;
  providerId: string;
  providerName?: string;
  providerEmail?: string;
}

export const EmailConversationView: React.FC<EmailConversationViewProps> = ({
  referralId,
  providerId,
  providerName = 'Provider',
  providerEmail
}) => {
  const { fmt } = useFormattedDate();
  const [emails, setEmails] = useState<Email[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [newSubject, setNewSubject] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [useHtml, setUseHtml] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [templates, setTemplates] = useState<any[]>([]);
  const [expandedEmail, setExpandedEmail] = useState<number | null>(null);
  // Sender identity: 'weavein' (org/SendGrid default) or 'me' (user's own mailbox)
  const [senderMode, setSenderMode] = useState<'weavein' | 'me'>('weavein');
  const [mailbox, setMailbox] = useState<MailboxStatus | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchEmails();
    fetchTemplates();
    // Check whether the current user has a personal mailbox connected
    inboxEmailAPI.status().then(setMailbox).catch(() => setMailbox(null));
    // Poll for new emails every 30 seconds
    const interval = setInterval(fetchEmails, 30000);
    return () => clearInterval(interval);
  }, [referralId, providerId]);

  useEffect(() => {
    scrollToBottom();
  }, [emails]);

  const fetchEmails = async () => {
    try {
      const response = await authFetch(`${config.api.baseUrl}/api/email/conversations?referral_id=${referralId}&provider_id=${providerId}`);

      if (response.ok) {
        const data = await response.json();
        setEmails(data.emails || []);
      }
    } catch (err) {
      console.error('Error fetching emails:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await authFetch(`${config.api.baseUrl}/api/email/templates`);

      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
  };

  const handleSendEmail = async () => {
    if (!newSubject.trim() || !newMessage.trim()) {
      alert('Please enter subject and message');
      return;
    }

    if (!providerEmail) {
      alert('Provider email not available');
      return;
    }

    setSending(true);
    try {
      // Send-as-user: route through the current user's own Outlook/Gmail mailbox.
      if (senderMode === 'me' && mailbox?.connected) {
        const bodyHtml = useHtml ? newMessage : newMessage.replace(/\n/g, '<br>');
        await inboxEmailAPI.send({ to: [providerEmail], subject: newSubject, body_html: bodyHtml });
        setNewSubject('');
        setNewMessage('');
        setSelectedTemplate('');
        await fetchEmails();
        return;
      }

      const response = await authFetch(`${config.api.baseUrl}/api/referral-communications`, {
        method: 'POST',
        jsonBody: {
            referral_id: referralId,
            provider_id: providerId,
            provider_name: providerName,
            method_type: 'email',
            contact_value: providerEmail,
            message_content: newMessage,
            template_id: selectedTemplate || undefined,
            subject: newSubject,
            use_html: useHtml
          }
      });

      if (response.ok) {
        setNewSubject('');
        setNewMessage('');
        setSelectedTemplate('');
        await fetchEmails();
      } else {
        const error = await response.json();
        alert(`Failed to send email: ${error.error || 'Unknown error'}`);
      }
    } catch (err: any) {
      console.error('Error sending email:', err);
      alert(err?.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const handleTemplateSelect = (templateId: string) => {
    const template = templates.find(t => t.template_name === templateId);
    if (template) {
      setSelectedTemplate(templateId);
      setNewSubject(template.subject_template);
      setNewMessage(useHtml ? template.body_html_template : template.body_text_template);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'sent':
      case 'delivered':
        return <CheckCircle className="w-3 h-3 text-green-600" />;
      case 'opened':
        return <Eye className="w-3 h-3 text-blue-600" />;
      case 'clicked':
        return <MousePointer className="w-3 h-3 text-purple-600" />;
      case 'failed':
      case 'bounced':
        return <AlertCircle className="w-3 h-3 text-red-600" />;
      default:
        return <Clock className="w-3 h-3 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent':
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'opened':
        return 'bg-blue-100 text-blue-800';
      case 'clicked':
        return 'bg-purple-100 text-purple-800';
      case 'failed':
      case 'bounced':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Mail className="w-12 h-12 text-gray-400 mx-auto mb-2 animate-pulse" />
          <p className="text-gray-600">Loading emails...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Email Thread */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {emails.length === 0 ? (
          <div className="text-center py-12">
            <Mail className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No email conversations yet</p>
            <p className="text-gray-400 text-sm">Send the first email to start the conversation</p>
          </div>
        ) : (
          emails.map((email) => (
            <Card
              key={email.id}
              className={`${
                email.direction === 'outbound'
                  ? 'bg-blue-50 border-blue-200 ml-8'
                  : 'bg-white border-gray-200 mr-8'
              } shadow-sm hover:shadow-md transition-shadow`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-sm">
                        {email.direction === 'outbound' ? 'You' : providerName}
                      </span>
                      <Badge variant="outline" className={`text-xs ${getStatusColor(email.status)}`}>
                        <div className="flex items-center gap-1">
                          {getStatusIcon(email.status)}
                          {email.status}
                        </div>
                      </Badge>
                      {email.has_attachments && (
                        <Badge variant="outline" className="text-xs">
                          <Paperclip className="w-3 h-3 mr-1" />
                          {email.attachments_count} attachment{email.attachments_count > 1 ? 's' : ''}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">
                      {email.direction === 'outbound' ? `To: ${email.to_email}` : `From: ${email.from_email}`}
                      {email.cc_emails && email.cc_emails.length > 0 && ` • CC: ${email.cc_emails.join(', ')}`}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {fmt.dateTime(email.created_at)}
                      {email.opened_at && ` • Opened: ${fmt.dateTime(email.opened_at)}`}
                      {email.clicked_at && ` • Clicked: ${fmt.dateTime(email.clicked_at)}`}
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-2">
                <h4 className="font-semibold text-sm mb-2 text-gray-900">{email.subject}</h4>

                <div
                  className={`text-sm text-gray-700 ${
                    expandedEmail === email.id ? '' : 'line-clamp-3'
                  }`}
                >
                  {email.body_html ? (
                    <div dangerouslySetInnerHTML={{ __html: email.body_html }} />
                  ) : (
                    <pre className="whitespace-pre-wrap font-sans">{email.body_text}</pre>
                  )}
                </div>

                {email.body_text.length > 200 && (
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => setExpandedEmail(expandedEmail === email.id ? null : email.id)}
                    className="p-0 h-auto text-xs text-blue-600 hover:text-blue-800 mt-2"
                  >
                    {expandedEmail === email.id ? 'Show less' : 'Show more'}
                  </Button>
                )}

                {/* Attachments */}
                {email.has_attachments && email.attachments.length > 0 && (
                  <div className="mt-3 space-y-1">
                    {email.attachments.map((att, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2 p-2 bg-gray-100 rounded text-xs"
                      >
                        <Paperclip className="w-3 h-3 text-gray-500" />
                        <span className="flex-1 font-medium">{att.filename}</span>
                        <span className="text-gray-500">{(att.size / 1024).toFixed(1)} KB</span>
                        {att.url && (
                          <a
                            href={att.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800"
                          >
                            Download
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Compose New Email */}
      <div className="border-t bg-white p-4">
        <div className="max-w-4xl mx-auto space-y-3">
          {/* Template Selector */}
          {templates.length > 0 && (
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-gray-700">Template:</label>
              <select
                value={selectedTemplate}
                onChange={(e) => handleTemplateSelect(e.target.value)}
                className="flex-1 text-sm border border-gray-300 rounded px-2 py-1"
              >
                <option value="">-- Select Template --</option>
                {templates.map(t => (
                  <option key={t.id} value={t.template_name}>
                    {t.template_name} ({t.template_type})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Subject */}
          <Input
            value={newSubject}
            onChange={(e) => setNewSubject(e.target.value)}
            placeholder="Subject"
            className="text-sm"
          />

          {/* Message Body */}
          <div className="relative">
            <Textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder={useHtml ? "HTML email body..." : "Email message..."}
              className="text-sm min-h-[100px]"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1 text-xs text-gray-600">
                <input
                  type="checkbox"
                  checked={useHtml}
                  onChange={(e) => setUseHtml(e.target.checked)}
                  className="rounded"
                />
                HTML
              </label>
              {mailbox?.connected && (
                <label className="flex items-center gap-1 text-xs text-gray-600">
                  <span>From:</span>
                  <select
                    value={senderMode}
                    onChange={(e) => setSenderMode(e.target.value as 'weavein' | 'me')}
                    className="text-xs border border-gray-300 rounded px-1 py-0.5"
                  >
                    <option value="weavein">WeaveIn (default)</option>
                    <option value="me">{mailbox.email || 'My mailbox'}</option>
                  </select>
                </label>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Button
                onClick={() => {
                  setNewSubject('');
                  setNewMessage('');
                  setSelectedTemplate('');
                }}
                variant="outline"
                size="sm"
                disabled={sending}
              >
                Clear
              </Button>
              <Button
                onClick={handleSendEmail}
                disabled={sending || !newSubject.trim() || !newMessage.trim()}
                className="bg-v2accent hover:bg-v2accent/90 text-white"
                size="sm"
              >
                {sending ? (
                  <>
                    <Clock className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Email
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
